// Computes a tenant's estimated monthly price from the Superadmin Pricing
// config (base/addon rate tiers, the driver:vehicle billable-units guard,
// and the multi-module discount) -- a live estimate off CURRENT counts,
// not a real invoice. There's no billing-cycle tracking yet (no snapshot
// of what a tenant owed at the start of a period), so this is
// deliberately a single "if we billed them right now" number rather than
// a real this-month/next-month split -- see the conversation this was
// built from for why that's the case today.
import { calculateBillableUnits, type PlatformBillingBasis } from './billableUnits';

export interface PricingTierInput {
  module_key: string;
  rate_type: 'base' | 'addon';
  tier_min: number;
  tier_max: number | null;
  unit_price: number;
}

export interface ModulePricingConfig {
  module_key: string;
  billing_basis: 'per_vehicle' | 'per_user' | 'flat';
  excluded_from_module_discount: boolean;
}

export interface DiscountTierInput {
  min_modules: number;
  max_modules: number | null;
  discount_percent: number;
}

export interface EnabledModuleInput {
  module_key: string;
  enabled_at: string;
}

export interface TenantPriceModuleLine {
  module_key: string;
  rate_type: 'base' | 'addon';
  price: number;
}

export interface TenantPriceResult {
  billableUnits: number;
  lines: TenantPriceModuleLine[];
  discountableSubtotal: number;
  excludedSubtotal: number;
  discountPercent: number;
  discountAmount: number;
  total: number;
}

// Marginal/graduated tiers -- e.g. 1-5 @ $30 + 6-15 @ $25, not "look up
// which bracket the total falls in and price everything at that rate".
// Matches how tax brackets work.
function priceTiered(tiers: PricingTierInput[], units: number): number {
  let total = 0;
  for (const t of [...tiers].sort((a, b) => a.tier_min - b.tier_min)) {
    if (units < t.tier_min) break;
    const bracketTop = t.tier_max ?? units;
    const unitsInBracket = Math.min(units, bracketTop) - t.tier_min + 1;
    if (unitsInBracket > 0) total += unitsInBracket * t.unit_price;
  }
  return total;
}

/**
 * enabledModules must already be filtered to paid modules only (exclude
 * Dashboard/Settings -- they're never priced) and include each module's
 * tenant_modules.created_at as enabled_at, so the earliest-enabled one can
 * be identified as the tenant's "base" module.
 */
export function calculateTenantMonthlyPrice(
  enabledModules: EnabledModuleInput[],
  tiers: PricingTierInput[],
  moduleConfigs: ModulePricingConfig[],
  discountTiers: DiscountTierInput[],
  platformBillingBasis: PlatformBillingBasis,
  activeDrivers: number,
  activeVehicles: number
): TenantPriceResult {
  const { billableUnits } = calculateBillableUnits(platformBillingBasis, activeDrivers, activeVehicles);

  const configByKey = new Map(moduleConfigs.map((c) => [c.module_key, c]));
  const tiersByModuleType = new Map<string, PricingTierInput[]>();
  tiers.forEach((t) => {
    const key = `${t.module_key}:${t.rate_type}`;
    const existing = tiersByModuleType.get(key);
    if (existing) existing.push(t);
    else tiersByModuleType.set(key, [t]);
  });

  const ordered = [...enabledModules].sort(
    (a, b) => new Date(a.enabled_at).getTime() - new Date(b.enabled_at).getTime()
  );

  const lines: TenantPriceModuleLine[] = ordered.map((m, index) => {
    const rateType: 'base' | 'addon' = index === 0 ? 'base' : 'addon';
    const config = configByKey.get(m.module_key);
    const moduleTiers = tiersByModuleType.get(`${m.module_key}:${rateType}`) ?? [];
    const price =
      config?.billing_basis === 'flat' ? moduleTiers[0]?.unit_price ?? 0 : priceTiered(moduleTiers, billableUnits);
    return { module_key: m.module_key, rate_type: rateType, price };
  });

  let discountableSubtotal = 0;
  let excludedSubtotal = 0;
  lines.forEach((line) => {
    if (configByKey.get(line.module_key)?.excluded_from_module_discount) {
      excludedSubtotal += line.price;
    } else {
      discountableSubtotal += line.price;
    }
  });

  const totalModuleCount = enabledModules.length;
  const discountTier = discountTiers.find(
    (d) => totalModuleCount >= d.min_modules && (d.max_modules === null || totalModuleCount <= d.max_modules)
  );
  const discountPercent = discountTier?.discount_percent ?? 0;
  const discountAmount = discountableSubtotal * (discountPercent / 100);

  return {
    billableUnits,
    lines,
    discountableSubtotal,
    excludedSubtotal,
    discountPercent,
    discountAmount,
    total: discountableSubtotal - discountAmount + excludedSubtotal,
  };
}
